// SegmentBubble (UI-C4) and LiveBubble (UI-C18, D-24): ONE element from the first sustained sound to the
// landed words. speaking (a scrolling level trace and "speaking · 0:04") -> writing (breath dots and
// "writing… · 4.2s", "still writing…" after 3 s) -> the text lands with a 350 ms fade, in the same bubble,
// in the colour and on the side it was born on — D-31 took the outline box away, and the live row is one
// text line tall so nothing jumps when the words arrive. The live stages are hidden from screen readers;
// the words are inserted fresh when they land, so the canvas (role=log) announces them once. `dir` sits on
// the bubble so its name, text and controls mirror together (critique decision 5).
//
// D-31 side rule (replacing the side-by-script rule of UI-C4): YOUR phrases sit at the inline end on the
// own colour and carry no name; everyone else's sit at the inline start on the surface with the speaker's
// name in that seat's colour. The direction of the text inside a bubble still follows its own language.
//
// Translation (MAJLIS-49, UI-C4, consultant ruling 8 of 2026-09-22): the ORIGINAL comes first and is never
// touched; its translation into the reader's language sits BENEATH it in `.tr` (the prototype's element), with a
// pending slot while the text is provisional (D-17) or the translation is on its way. This supersedes the
// earlier plan here to swap the two lines' roles by reading language (critique decision 1).
import { useId, type CSSProperties, type ReactNode } from "react";
import { LEVEL_TICK_MS, WAVE_BARS } from "../constants";
import { languageName } from "../data/languages";
import { dirOf, useI18n, type Translate } from "../i18n";
import type { BubbleView } from "../room/liveBubble";
import type { Bubble as BubbleModel, SeatView, TranslationView } from "../room/model";
import type { ReplayControl } from "../media/replay";
import { ReplayButton, ReplayReason } from "./ReplayButton";
import { VoiceTranslationIcon } from "./icons";

/** MAJLIS-64 (UI-C6, UI-C22): what the bubble shows of the voice for its translation. */
export interface BubbleVoice {
  /** UI-C6: Tap Voice is offered (room_state.voice, another seat's phrase, a translation on screen). */
  offered: boolean;
  /** UI-C22: this phrase's translation is sounding now (the queue's `speaking()`). */
  spoken: boolean;
  /** UI-C6: its voice was asked for and has not started or ended yet (the queue's `pending()`). */
  loading: boolean;
  onTap: () => void;
}

function clockTime(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function stageText(view: BubbleView, t: Translate): string {
  switch (view.stage) {
    case "speaking":
      return `${t("speaking")} · ${clockTime(view.ms)}`;
    case "writing":
      return `${t("writing")} · ${t("seconds", { s: (view.ms / 1000).toFixed(1) })}`;
    case "still_writing":
      return t("stillWriting");
    case "failed":
      return t("failed");
    case "text":
      return "";
  }
}

export function speakerLabel(seat: SeatView | undefined, b: BubbleModel, t: Translate, passive = false): string {
  const name = seat?.name ?? "";
  const voice = b.segment?.voice;
  // MAJLIS-137 (UI-C39): in a passive room the phone IS the room - a voice is just "Voice n", and a phrase the engine
  // gave no voice (placed late, or unplaceable) carries no label at all rather than the phone's name
  if (passive) return voice ? voice.display_name || t("voiceBare", { n: voice.n }) : "";
  if (voice) return voice.display_name || t("voiceN", { name, n: voice.n });
  if (seat?.kind === "shared") return t("sharedPhone", { name }); // until the voice resolves
  return name;
}

/** UI-M5 (D-31): 3 px bars follow the real level history (about 9 Hz) and scroll toward the inline end.
 * While the phrase is being written the trace gives way to breath dots.
 *
 * Only THIS device's own seat has a level history — no level for any other seat reaches the web in M1 — so
 * someone else's live bubble draws the same bars on a CSS keyframe instead: an INDETERMINATE trace, which
 * says "this person is speaking" without claiming to be their voice. It is not a measurement of anybody, and
 * it asks nothing new of the protocol. The stage words carry the state in every case. */
export function Waveform({ levels, writing, still }: { levels: number[] | null; writing: boolean; still: boolean }) {
  const spec = { "data-spec": "UI-M5", "data-tick-ms": LEVEL_TICK_MS } as const;
  if (writing) {
    return (
      <div className="live-dots" {...spec}>
        {[0, 1, 2].map((i) => (
          <i key={i} style={{ "--i": i } as CSSProperties} />
        ))}
      </div>
    );
  }
  const history = levels && levels.length > 0 ? levels : null;
  if (!history) {
    return (
      <div className="wave indeterminate" {...spec}>
        {Array.from({ length: WAVE_BARS }, (_, i) => (
          <i key={i} style={{ "--i": i } as CSSProperties} />
        ))}
      </div>
    );
  }
  const bars: number[] = [];
  for (let i = 0; i < WAVE_BARS; i++) {
    const v = history[history.length - WAVE_BARS + i] ?? 0;
    bars.push(Math.max(0.15, Math.min(1, v * 4)));
  }
  return (
    <div className={`wave${still ? " still" : ""}`} {...spec}>
      {bars.map((h, i) => (
        <i key={i} style={{ "--h": h.toFixed(2), "--i": i } as CSSProperties} />
      ))}
    </div>
  );
}

export function Bubble({
  bubble,
  seat,
  view,
  isMe,
  levels,
  reduced,
  translation,
  fromLive,
  replay,
  voice: voiceOf,
  onNameVoice,
  passive = false,
}: {
  /** MAJLIS-137 (UI-C39): a passive room - its phrases are drawn as other people's (the caller passes isMe false) and
   * labelled "Voice n". */
  passive?: boolean;
  /** MAJLIS-65 (UI-C16): offered only to the shared seat's HOLDER and to a HOST - the voice label becomes a button that
   * opens the NameVoiceSheet. Absent: the label is plain text (everyone else, and every personal seat). */
  onNameVoice?: () => void;
  /** MAJLIS-64: the voice of this phrase's translation (absent: none offered, none spoken). */
  voice?: BubbleVoice;
  /** UI-C5: the one replay player of the canvas (exactly one phrase plays at a time). */
  replay: ReplayControl;
  bubble: BubbleModel;
  /** the words land in a bubble the reader already saw (fade), not a new one (slide up, UI-M1) */
  fromLive: boolean;
  seat: SeatView | undefined;
  view: BubbleView;
  isMe: boolean;
  levels: number[] | null;
  reduced: boolean;
  /** MAJLIS-49: what sits under the original for this reader (room/model.ts `translationView`). */
  translation: TranslationView;
}) {
  const { lang, t } = useI18n();
  const reasonId = useId(); // the Replay button and its reason row are no longer siblings
  const seg = bubble.segment;
  const textLang = seg?.lang ?? seat?.speak_lang ?? "und";
  const dir = dirOf(textLang);
  const colour = seat?.colour ?? 0;
  const label = speakerLabel(seat, bubble, t, passive);
  const spoken = voiceOf?.spoken === true && view.stage === "text";
  const voice = seg?.voice ?? null;
  // D-31: your own bubble is already yours by its colour and its side, so it carries no name — unless your
  // own seat is a shared one, where the voice is the thing being named (D-18).
  const named = (!isMe || voice !== null || seat?.kind === "shared") && label !== "";
  const badge = voice && (
    <span className="voice-badge" data-spec="D-18">
      {voice.n}
    </span>
  );
  // UI-C16: the label of a voice the reader may name is a button (the prototype's `button.who.voice`); a 44 px hit area
  // that takes no more room in the bubble head than the text it carries.
  const who = !named ? null : voice && onNameVoice ? (
    <button
      type="button"
      className="who who-name"
      dir="auto"
      data-voice={voice.id}
      title={t("nameThisVoice")}
      aria-label={t("nameVoiceButton", { label })}
      onClick={onNameVoice}
      data-spec="UI-C16 P-16 D-18"
    >
      {badge}
      <span className="vname">{label}</span>
    </button>
  ) : (
    <span className="who" dir="auto" data-voice={voice?.id}>
      {badge}
      {voice ? <span className="vname">{label}</span> : label}
    </span>
  );
  const classes = [
    "bubble",
    isMe ? "own" : "",
    `seat-c${colour}`,
    `stage-${view.stage}`,
    // P-19 (MAJLIS-89): a phrase the join was handed is history, already there when the canvas opens: no slide
    fromLive ? "was-live" : bubble.earlier ? "earlier" : "slide-up",
    view.fading ? "fading" : "", // MAJLIS-73: the voice gate took it away; it fades, never jumps
    seg?.state === "provisional" ? "provisional" : "",
    // a shared seat's voices are tints of ITS seat colour, told apart by the numbered badge
    voice ? `voice-${Math.min(voice.n, 4)}` : "",
    // UI-C22: the phrase being spoken to this reader - an accent outline for exactly its audio span, nothing moves
    spoken ? "spoken" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const head = who && <header className="bubble-head">{who}</header>;

  const when = bubble.wall !== undefined && (
    <time className="when">{new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(bubble.wall)}</time>
  );
  /* The meta row (D-31): the status word at its inline start, then Replay, Voice (M2) and the time grouped
     at its inline end. It is laid out in the INTERFACE's direction, not the phrase's — the controls belong
     to the reader, so they sit where the reader's other controls sit, whatever language the phrase is in.
     The text inside each cell still carries its own language. */
  const metaRow = (status?: ReactNode) => (
    <>
      <footer className="bubble-meta" dir={dirOf(lang)}>
        {status}
        <ReplayButton segmentId={bubble.id} ready={bubble.audioMs !== undefined} replay={replay} reasonId={reasonId} />
        {/* UI-C6: Voice sits between Replay and the time. The spoken phrase carries its tag instead (the mock-up) */}
        {voiceOf?.offered && !spoken && translation.kind === "text" && (
          <button
            type="button"
            className={`icon-button voice${voiceOf.loading ? " loading" : ""}`}
            aria-label={t("voiceTranslation", { lang: languageName(translation.lang, lang) })}
            aria-busy={voiceOf.loading || undefined}
            onClick={() => {
              if (!voiceOf.loading) voiceOf.onTap();
            }}
            data-spec="UI-C6 P-11 D-08 D-20 T-305"
          >
            <VoiceTranslationIcon />
          </button>
        )}
        {when}
      </footer>
      <ReplayReason replay={replay} id={reasonId} />
    </>
  );

  let body;
  if (view.stage === "failed") {
    // A failed phrase keeps its Replay once audio_ready arrives (EM-23, consultant ruling 2026-09-20): the
    // engine could not write it, so his own voice is all that is left of it. Only a LIVE bubble has none,
    // because there is no clip yet.
    body = (
      <div className="bubble-body failed" key="failed">
        {head}
        <div className="line-row">
          <p className="failed-text">{t("failed")}</p>
        </div>
        {metaRow()}
      </div>
    );
  } else if (view.stage !== "text" || !seg) {
    body = (
      <div className="bubble-body live" key="live" aria-hidden="true">
        {head}
        <div className="live-row">
          <Waveform levels={levels} writing={view.stage !== "speaking"} still={reduced || levels === null} />
          <span className="stage">{stageText(view, t)}</span>
        </div>
      </div>
    );
  } else {
    const original = (
      <p
        className={`line primary${bubble.revisionCount ? " revising" : ""}`}
        lang={seg.lang}
        dir={dirOf(seg.lang)}
        key={`o${bubble.revisionCount}`}
        data-spec="UI-P3 P-7"
      >
        {seg.text}
      </p>
    );
    body = (
      <div className="bubble-body landed" key="landed">
        {head}
        {/* a line row holds its paragraph ALONE, so the words get the bubble's full width (UI-C5, D-31) */}
        <div className="line-row">{original}</div>
        {translation.kind === "text" && (
          <div className="tr" data-spec="P-10 D-06 UI-M2 D-17">
            {/* keyed by language: a reading-language change re-renders it, and fades it in again (UI-M2) */}
            <p className="tr-text" lang={translation.lang} dir={dirOf(translation.lang)} key={translation.lang}>
              {translation.text}
            </p>
          </div>
        )}
        {translation.kind === "pending" && (
          <div className="tr pending" data-spec="P-10 D-06 D-17">
            <div
              className="translation-slot pending"
              role="img"
              aria-label={t(seg.state === "provisional" ? "translationPending" : "translationComing")}
            >
              <i />
              <i />
              <i />
            </div>
          </div>
        )}
        {metaRow(
          <>
            {spoken && (
              <span className="speaking-tag" data-spec="UI-C22 D-43">
                <i aria-hidden="true" />
                <i aria-hidden="true" />
                <i aria-hidden="true" />
                {t("speakingToYou")}
              </span>
            )}
            {bubble.revised && (
              <span className="revised" data-spec="UI-M3 P-9">
                {t("revised")}
              </span>
            )}
          </>,
        )}
      </div>
    );
  }

  return (
    <article
      className={classes}
      dir={dir}
      data-id={bubble.id}
      data-stage={view.stage}
      data-spec="UI-C18 UI-C4 C-2.4 P-6 P-7 UI-M1 D-24"
    >
      {body}
    </article>
  );
}
